import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/admin';
import api from '../../services/api';
import {
  UserCircleIcon,
  EnvelopeIcon,
  KeyIcon,
  ShieldCheckIcon,
  CameraIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  SparklesIcon,
  CalendarIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { getImageUrl } from '../../services/api';

interface ProfileData {
  id?: number;
  student_id?: string;
  email: string;
  name: string;
  role: string;
  last_login?: string;
  phone?: string;
  age?: number;
  date_of_birth?: string;
  village?: string;
  district?: string;
  province?: string;
  profile_image?: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    phone: '',
    age: '',
    date_of_birth: '',
    village: '',
    district: '',
    province: '',
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsSmallScreen(width < 480); // Extra small screens
      setIsMobile(width < 768); // Mobile devices
      setIsTablet(width >= 768 && width < 1024); // Tablet devices
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Effect to update profile image preview when profile data changes
  useEffect(() => {
    if (profileData?.profile_image && !imagePreview) {
      console.log('Setting image preview from profile data:', profileData.profile_image);
    }
  }, [profileData, imagePreview]);

  const fetchProfileData = async () => {
    try {
      console.log('Fetching profile data...');
      setIsLoading(true);
      
      const data = await adminService.getProfile();
      console.log('Profile data received:', data);
      
      if (!data) {
        setMessage({ type: 'error', text: 'ບໍ່ໄດ້ຮັບຂໍ້ມູນໂປຣໄຟລ໌ຈາກເຊີເວີ' });
        setIsLoading(false);
        return;
      }
      
      // Extract user data from the response
      const userData = data.user || data;
      setProfileData(userData);
      
      // Format date of birth for date input if available
      let formattedDate = userData.date_of_birth;
      if (userData.date_of_birth) {
        try {
          const date = new Date(userData.date_of_birth);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        } catch (e) {
          console.error('Error formatting date:', e);
        }
      }
      
      setFormData(prev => ({ 
        ...prev, 
        name: userData.name || '',
        phone: userData.phone || '',
        age: userData.age?.toString() || '',
        date_of_birth: formattedDate || '',
        village: userData.village || '',
        district: userData.district || '',
        province: userData.province || '',
      }));
      
      // Update the user context with profile image if available
      if (userData.profile_image && user) {
        console.log('Updating user context with profile image:', userData.profile_image);
        updateUser({
          ...user,
          name: userData.name || user.name,
          profile_image: userData.profile_image
        });
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Failed to fetch profile data' });
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      console.log('Image selected:', {
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024).toFixed(2)} KB`,
      });
      
      // Get file extension and validate it
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      console.log(`File extension: .${fileExtension}`);
      
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      if (!allowedExtensions.includes(fileExtension)) {
        setMessage({ 
          type: 'error', 
          text: `Invalid file extension (.${fileExtension}). Allowed: ${allowedExtensions.join(', ')}` 
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setMessage({ 
          type: 'error', 
          text: `Invalid file type (${file.type}). Allowed: ${allowedTypes.join(', ')}` 
        });
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setMessage({ 
          type: 'error', 
          text: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds 5MB limit` 
        });
        return;
      }
      
      setProfileImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.onerror = (error) => {
        console.error('Error creating image preview:', error);
        setMessage({ type: 'error', text: 'Failed to preview image' });
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImageSelection = () => {
    setProfileImage(null);
    setImagePreview(null);
    
    // Reset the file input
    const fileInput = document.getElementById('profile-image') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      console.log('Starting profile update...');
      
      // Handle password change separately if requested
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          setMessage({ type: 'error', text: 'ລະຫັດຜ່ານໃໝ່ບໍ່ກົງກັນ' });
          setIsSaving(false);
          return;
        }
        if (!formData.currentPassword) {
          setMessage({ type: 'error', text: 'Current password is required' });
          setIsSaving(false);
          return;
        }

        try {
          console.log('Changing password...');
          await adminService.changePassword({
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword
          });
          console.log('Password changed successfully');
          setMessage({ type: 'success', text: 'ປ່ຽນລະຫັດຜ່ານສຳເລັດແລ້ວ' });
          
          // Clear password fields
          setFormData(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }));
        } catch (passwordError: any) {
          console.error('Password change error:', passwordError);
          const errorMsg = passwordError.response?.data?.message || 'ການປ່ຽນລະຫັດຜ່ານລົ້ມເຫລວ';
          setMessage({ type: 'error', text: errorMsg });
          setIsSaving(false);
          return;
        }
      }

      // Create FormData if we have a profile image
      if (profileImage) {
        console.log('Profile update with image...');
        const formDataObj = new FormData();
        
        // Add all form data to FormData
        formDataObj.append('name', formData.name);
        
        // Add additional profile fields
        if (formData.phone) formDataObj.append('phone', formData.phone);
        if (formData.age) formDataObj.append('age', formData.age);
        if (formData.date_of_birth) formDataObj.append('date_of_birth', formData.date_of_birth);
        if (formData.village) formDataObj.append('village', formData.village);
        if (formData.district) formDataObj.append('district', formData.district);
        if (formData.province) formDataObj.append('province', formData.province);
        
        // Add the profile image - ensure correct field name matching backend
        formDataObj.append('profile_image', profileImage);
        
        // Log what we're sending
        console.log('FormData entries:');
        for (const [key, value] of formDataObj.entries()) {
          if (typeof value === 'object' && value instanceof File) {
            console.log(`${key}: File name=${value.name}, type=${value.type}, size=${value.size} bytes`);
          } else {
            console.log(`${key}: ${value}`);
          }
        }
        
        try {
          // Use the method for updating profile with image
          const response = await adminService.updateProfileWithImage(formDataObj);
          console.log('Profile update response:', response);
          
          setMessage({ type: 'success', text: 'ອັບເດດໂປຣໄຟລ໌ສຳເລັດແລ້ວ' });
          
          // Update user context if name was changed
          if (user && formData.name !== user.name) {
            updateUser({ ...user, name: formData.name });
          }
          
          // Refresh profile data to show updated information
          await fetchProfileData();
          
          setIsEditing(false);
          clearImageSelection();
        } catch (imageError: any) {
          console.error('Profile image update error:', imageError);
          handleUpdateError(imageError);
        }
      } else {
        // For updates without image, use the standard method with all fields
        console.log('Profile update without image...');
        const updateData = {
          name: formData.name,
          phone: formData.phone || undefined,
          age: formData.age ? Number(formData.age) : undefined,
          date_of_birth: formData.date_of_birth || undefined,
          village: formData.village || undefined,
          district: formData.district || undefined,
          province: formData.province || undefined,
        };
        console.log('Update data:', updateData);
        
        try {
          const response = await adminService.updateProfile(updateData);
          console.log('Profile update response:', response);
          
          setMessage({ type: 'success', text: 'ອັບເດດໂປຣໄຟລ໌ສຳເລັດແລ້ວ' });
          
          // Update user context if name was changed
          if (user && formData.name !== user.name) {
            updateUser({ ...user, name: formData.name });
          }
          
          // Refresh profile data
          await fetchProfileData();
          
          setIsEditing(false);
        } catch (updateError: any) {
          console.error('Profile update error:', updateError);
          handleUpdateError(updateError);
        }
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      handleUpdateError(error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleUpdateError = (error: any) => {
    // Provide more specific error messages
    if (error.response?.data?.message) {
      setMessage({
        type: 'error',
        text: error.response.data.message
      });
    } else if (error.response?.status === 413) {
      setMessage({
        type: 'error',
        text: 'ຂະໜາດໄຟລ໌ໃຫຍ່ເກີນໄປ. ກະລຸນາເລືອກຮູບທີ່ນ້ອຍກວ່າ.'
      });
    } else if (error.response?.status === 400) {
      setMessage({
        type: 'error',
        text: 'ໄຟລ໌ ຫຼື ຂໍ້ມູນບໍ່ຖືກຕ້ອງ. ກະລຸນາກວດສອບຂໍ້ມູນຂອງທ່ານ ແລະ ລອງໃໝ່.'
      });
    } else if (error.response?.status === 404) {
      setMessage({
        type: 'error',
        text: 'ບໍ່ພົບ API endpoint. ກະລຸນາຕິດຕໍ່ຜູ້ສະໜັບສະໜູນ.'
      });
    } else if (error.message) {
      setMessage({
        type: 'error',
        text: `ການອັບໂຫລດລົ້ມເຫລວ: ${error.message}`
      });
    } else {
      setMessage({
        type: 'error',
        text: 'ການອັບເດດໂປຣໄຟລ໌ລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.'
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (profileData) {
      setFormData(prev => ({
        ...prev,
        name: profileData.name || '',
        phone: profileData.phone || '',
        age: profileData.age?.toString() || '',
        date_of_birth: profileData.date_of_birth || '',
        village: profileData.village || '',
        district: profileData.district || '',
        province: profileData.province || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    }
    clearImageSelection();
    setMessage(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8">
      {/* Header with gradient background */}
      <div className="mb-6 sm:mb-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">ໂປຣໄຟລ໌</h1>
            <p className="mt-2 text-sm sm:text-base text-indigo-100">ຈັດການຂໍ້ມູນໂປຣໄຟລ໌ຂອງທ່ານ</p>
          </div>
          <ShieldCheckIcon className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 text-yellow-300 opacity-50 flex-shrink-0" />
        </div>
      </div>

      {/* Profile Content */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Profile Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">ຂໍ້ມູນສ່ວນຕົວ</h2>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105 shadow-md w-full sm:w-auto justify-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                ແກ້ໄຂໂປຣໄຟລ໌
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2 w-full sm:w-auto">
                <button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-md justify-center"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ກຳລັງບັນທຶກ...
                    </>
                  ) : (
                    'ບັນທຶກການປ່ຽນແປງ'
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105 shadow-sm justify-center"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  ຍົກເລີກ
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profile Form */}
        <div className="p-4 sm:p-6">
          {message && (
            <div className={`mb-4 rounded-md p-4 ${
              message.type === 'success' ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className={`text-sm ${
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>{message.text}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ຮູບໂປຣໄຟລ໌
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center shadow-md flex-shrink-0">
                  {imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Failed to load image preview');
                        e.currentTarget.src = '';
                        e.currentTarget.onerror = null;
                      }}
                    />
                  ) : profileData?.profile_image ? (
                    <img 
                      src={getImageUrl(profileData.profile_image)} 
                      alt="Profile" 
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        console.error('Failed to load profile image:', profileData.profile_image);
                        console.error('Attempted URL:', getImageUrl(profileData.profile_image || ''));
                        e.currentTarget.src = '';
                        e.currentTarget.onerror = null;
                        e.currentTarget.parentElement?.classList.add('border', 'border-red-300');
                      }}
                    />
                  ) : (
                    <UserCircleIcon className="w-16 h-16 text-gray-400" />
                  )}
                </div>
                {isEditing && (
                  <div className="flex-1 min-w-0">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageChange}
                      className="hidden"
                      id="profile-image"
                    />
                    <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                      <label
                        htmlFor="profile-image"
                        className="cursor-pointer inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105 justify-center"
                      >
                        <CameraIcon className="h-4 w-4 mr-2" />
                        {imagePreview ? 'ປ່ຽນຮູບພາບ' : 'ອັບໂຫລດຮູບພາບ'}
                      </label>
                      
                      {imagePreview && (
                        <button
                          type="button"
                          onClick={clearImageSelection}
                          className="inline-flex items-center px-3 sm:px-4 py-2 border border-red-300 rounded-lg shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all transform hover:scale-105 justify-center"
                        >
                          <XMarkIcon className="h-4 w-4 mr-2" />
                          ລຶບ
                        </button>
                      )}
                    </div>
                    <p className="mt-2 sm:mt-1 text-xs text-gray-500">
                      JPG, PNG, GIF ຫຼື WebP. ຂະໜາດສູງສຸດ 5MB.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  ຊື່
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    aria-label="Full name"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <UserCircleIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  ອີເມວ
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={profileData?.email}
                    disabled
                    aria-label="Email address"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500"
                  />
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  ເບີໂທລະສັບ
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    aria-label="Phone number"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <PhoneIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700">
                  ອາຍຸ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="age"
                    id="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    aria-label="Age"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700">
                  ວັນເດືອນປີເກີດ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD (ຫຼື ຄລິກເພື່ອເລືອກວັນທີ)"
                    name="date_of_birth"
                    id="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    onFocus={(e) => {
                      if (isEditing) {
                        e.target.type = 'date';
                        e.target.click();
                      }
                    }}
                    onBlur={(e) => {
                      if (isEditing) {
                        e.target.type = 'text';
                      }
                    }}
                    aria-label="Date of birth"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  ສາມາດພິມວັນທີໄດ້ ຫຼື ຄລິກເພື່ອເລືອກຈາກປະຕິທິນ
                </p>
              </div>

              <div>
                <label htmlFor="village" className="block text-sm font-medium text-gray-700">
                  ບ້ານ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="village"
                    id="village"
                    value={formData.village}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    aria-label="Village"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <MapPinIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label htmlFor="district" className="block text-sm font-medium text-gray-700">
                  ເມືອງ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="district"
                    id="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    aria-label="District"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <MapPinIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label htmlFor="province" className="block text-sm font-medium text-gray-700">
                  ແຂວງ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="province"
                    id="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    aria-label="Province"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <MapPinIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ລະຫັດຜູ້ໃຊ້
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={profileData?.student_id || `STD-${String(profileData?.id || user?.id).padStart(5, '0')}`}
                    disabled
                    aria-label="Student ID"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500"
                  />
                  <UserCircleIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ສິດທິພິເສດ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={profileData?.role}
                    disabled
                    aria-label="User role"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500"
                  />
                  <ShieldCheckIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ເຂົ້າສູ່ລະບົບຄັ້ງສຸດທ້າຍ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={profileData?.last_login 
                      ? new Date(profileData.last_login).toLocaleDateString() 
                      : 'Not available'}
                    disabled
                    aria-label="Last login date"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500"
                  />
                  <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>
            </div>

            {/* Change Password Section */}
            {isEditing && (
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">ປ່ຽນລະຫັດຜ່ານ</h2>
                <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-gray-700">
                      ລະຫັດຜ່ານປັດຈຸບັນ
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        name="currentPassword"
                        id="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        aria-label="Current password"
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <KeyIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                      ລະຫັດຜ່ານໃໝ່
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        name="newPassword"
                        id="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        aria-label="New password"
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <KeyIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      ຢືນຢັນລະຫັດຜ່ານໃໝ່
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        name="confirmPassword"
                        id="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        aria-label="ຢືນຢັນລະຫັດຜ່ານໃໝ່"
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <KeyIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Admin Tips */}
      <div className="mt-6 sm:mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-indigo-200">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0">
          <div className="flex-shrink-0 flex justify-center sm:justify-start">
            <div className="bg-white rounded-full p-2 sm:p-3 shadow-sm">
              <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600" />
            </div>
          </div>
          <div className="sm:ml-6 text-center sm:text-left">
            <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mb-2">
              ຄຳແນະນຳດ້ານຄວາມປອດໄພສຳລັບຜູ້ເບິ່ງແຍງລະບົບ
            </h3>
            <p className="text-sm sm:text-base text-gray-600">
              ເປັນຜູ້ເບິ່ງແຍງລະບົບ, ບັນຊີຂອງທ່ານມີສິດທິພິເສດ. ກະລຸນາໃຊ້ລະຫັດຜ່ານທີ່ເຂັ້ມແຂງ ແລະ ບໍ່ຊ້ຳກັນ
              ແລະ ຮັກສາຂໍ້ມູນໂປຣໄຟລ໌ຂອງທ່ານໃຫ້ທັນສະໄໝເພື່ອຄວາມປອດໄພໃນການກູ້ຄືນບັນຊີຖ້າຈຳເປັນ.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;